import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetch } from "../../../hooks/api";
import logo from "../../../assets/images/logo/Trellis_Logomark_color.png";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";
import { SearchInput, TextField } from "../../fields/base/TextField";
import { useAppSelector } from "../../../redux/hooks";
import { Modal } from "../../core/basic/Modal";
import { Button } from "../../core/basic/Button";
import { Center, HStack, styled, VStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { errorToast, successToast } from "../../../utils/toasts";
import { FaPencil, FaPlus } from "react-icons/fa6";
import Card from "../../core/basic/Card";
import { GridList, GridListItem } from "react-aria-components";
import { useSimpleCan } from "../../../config/casl/casl";

const normalizeChannel = (channel) => {
  switch (channel.toLowerCase()) {
    case "amazon":
      return "AMZ";
    default:
      return channel;
  }
};

const CategoryListItem = styled(GridListItem, {
  base: {
    display: "flex",
    gap: "small",
    justifyContent: "space-between",
    padding: "{radii.standard}",
    fontSize: "small",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&[data-selected]": {
      backgroundColor: "trellisPalette.3",
      color: "white",
    },
    "&[data-focus-visible='true']": {
      outline: "2px solid {colors.trellisPalette.3}",
    },
  },
});

const EditCategoryDialog = ({ category, onClose, mutation }) => {
  const { t } = useTranslation(["merchandising", "common"]);
  const [newCategoryName, setNewCategoryName] = useState(category.name);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleRename = useCallback(() => {
    mutation.mutate(
      {
        method: "patch",
        id: category.id,
        data: { name: newCategoryName },
      },
      {
        onSuccess: () => {
          successToast({ message: t(($) => $.merchandising["Category has been renamed."]) });
          onClose();
        },
      },
    );
  }, [category.id, mutation, newCategoryName, onClose, t]);

  const handleDelete = useCallback(() => {
    mutation.mutate(
      {
        method: "delete",
        id: category.id,
      },
      {
        onSuccess: () => {
          successToast({ message: t(($) => $.merchandising["Category has been deleted."]) });
          onClose();
        },
      },
    );
  }, [category.id, mutation, onClose, t]);

  return (
    <Modal
      isOpen
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title={t(($) => $.merchandising["Edit Category - {name}"], { name: category.name })}
      css={{ width: "40vw" }}
    >
      <VStack paddingX="large" alignItems="stretch">
        {showConfirmation ?
          <>
            <h4 className={css({ margin: 0 })}>
              {t(
                ($) =>
                  $.merchandising[
                    "Are you sure you want to delete this category? All products in this category will be moved to the Uncategorized category."
                  ],
              )}
            </h4>
            <Center gap={"medium"}>
              <Button variant="secondary" onPress={() => setShowConfirmation(false)}>
                {t(($) => $.common.actions.Cancel)}
              </Button>
              <Button isPending={mutation.isPending} onPress={handleDelete}>
                {t(($) => $.common.actions.Confirm)}
              </Button>
            </Center>
          </>
        : <>
            <TextField
              label={t(($) => $.merchandising["Category Name"])}
              defaultValue={category.name}
              autoFocus
              onChange={setNewCategoryName}
            />
            <Center gap={"medium"}>
              <Button variant="secondary" onPress={() => setShowConfirmation(true)}>
                {t(($) => $.merchandising["Delete"])}
              </Button>
              <Button isPending={mutation.isPending} onPress={handleRename}>
                {t(($) => $.common.actions.Save)}
              </Button>
            </Center>
          </>
        }
      </VStack>
    </Modal>
  );
};

const NewCategoryDialog = ({ onClose, mutation, marketplace, channel }) => {
  const { t } = useTranslation(["merchandising", "common"]);
  const [newCategoryName, setNewCategoryName] = useState("");

  const handleRename = useCallback(() => {
    mutation.mutate(
      {
        method: "post",
        data: {
          marketplace: marketplace,
          channel: channel,
          name: newCategoryName,
        },
      },
      {
        onSuccess: () => {
          successToast({ message: t(($) => $.merchandising["Category has been created."]) });
          onClose();
        },
      },
    );
  }, [channel, marketplace, mutation, newCategoryName, onClose, t]);

  return (
    <Modal
      isOpen
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title={t(($) => $.merchandising["New Category"])}
      css={{ width: "40vw" }}
    >
      <VStack paddingX="large" alignItems="stretch">
        <TextField
          id={"category-name"}
          label={t(($) => $.merchandising["Category Name"])}
          autoFocus
          onChange={setNewCategoryName}
        />

        <Center>
          <Button isPending={mutation.isPending} onPress={handleRename}>
            {t(($) => $.common.actions.Save)}
          </Button>
        </Center>
      </VStack>
    </Modal>
  );
};

/**
 * Shows a list of selectable categories.
 */
const CategorySelector = ({ onCategorySelected, currentCategory }) => {
  const { t } = useTranslation(["merchandising", "common"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);
  const userCanCreateCategories = useSimpleCan("create", "Category");
  const userCanUpdateCategories = useSimpleCan("update", "Category");

  const queryClient = useQueryClient();

  const { isLoading, data } = useFetch(
    ["searchCategories", searchQuery, normalizeChannel(channel.currentChannel), marketPlace.marketPlace],
    "/api/v2/categories/",
    {
      name: searchQuery,
      channel: normalizeChannel(channel.currentChannel),
      marketplace: marketPlace.marketPlace,
    },
  );

  const categoryEditModification = useMutation({
    mutationFn: async (action) => {
      if (action.method === "patch") {
        return api.patch(`/api/v2/categories/${action.id}/`, action.data);
      } else if (action.method === "delete") {
        return api.remove(`/api/v2/categories/${action.id}/`);
      } else if (action.method === "post") {
        return api.post(`/api/v2/categories/`, action.data);
      }
      throw new Error("Action with an unknown method.");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["searchCategories"],
      });
    },

    onError: async (error) => {
      errorToast({
        title: t(($) => $.merchandising["Error"]),
        message: error.response.data["_msg"],
        error,
      });
    },
  });

  // Reset our state if the marketplace or channel changes.
  useEffect(() => {
    onCategorySelected(null);
    setSearchQuery("");
  }, [marketPlace, channel, onCategorySelected]);

  return (
    <>
      {categoryToEdit !== null ?
        <EditCategoryDialog
          category={categoryToEdit}
          onClose={() => setCategoryToEdit(null)}
          mutation={categoryEditModification}
          marketplace={marketPlace.marketPlace}
          channel={normalizeChannel(channel.currentChannel)}
        />
      : null}
      {showNewCategory && userCanCreateCategories ?
        <NewCategoryDialog
          onClose={() => setShowNewCategory(false)}
          mutation={categoryEditModification}
          marketplace={marketPlace.marketPlace}
          channel={normalizeChannel(channel.currentChannel)}
        />
      : null}
      <Card>
        <VStack alignItems="stretch">
          <HStack justify={"space-between"}>
            <p className={css({ margin: 0 })}>{t(($) => $.merchandising["Categories"])}</p>
            {userCanCreateCategories && (
              <Button variant={"contentOnly"} onPress={() => setShowNewCategory(true)}>
                <FaPlus size={16} />
              </Button>
            )}
          </HStack>

          <SearchInput
            label={t(($) => $.merchandising["Search Categories"])}
            placeholder={t(($) => $.merchandising["Query..."])}
            onChange={setSearchQuery}
            value={searchQuery}
          />
          {!isLoading ?
            <GridList
              aria-label={t(($) => $.merchandising["Categories"])}
              items={data?.data?.results}
              className={css({ width: "100%", maxHeight: "300px", overflowY: "auto" })}
              selectionMode={"single"}
              selectedKeys={[currentCategory]}
              onSelectionChange={(selectedKeys) => {
                if (selectedKeys === "all") {
                  onCategorySelected(data?.data?.results[0]?.id);
                } else if (selectedKeys.size > 0) {
                  const selectedKey = selectedKeys.values().next().value;
                  onCategorySelected(selectedKey);
                } else {
                  onCategorySelected(null);
                }
              }}
            >
              {(v) => (
                <CategoryListItem textValue={v.name} key={v.id}>
                  {v.name}
                  {userCanUpdateCategories && (
                    <HStack>
                      {v.name !== "Uncategorized" ?
                        <Button variant={"contentOnly"} onPress={() => setCategoryToEdit(v)}>
                          <FaPencil />
                        </Button>
                      : null}
                      <span>{v.total_products}</span>
                    </HStack>
                  )}
                </CategoryListItem>
              )}
            </GridList>
          : <div>
              <img src={logo} alt={t(($) => $.merchandising["Loading..."])} className="rotate" width="80" />
            </div>
          }
        </VStack>
      </Card>
    </>
  );
};

export default CategorySelector;
